import { useState } from "react";
import { useDispatch } from "react-redux";
import { useNavigate, useParams, useSearchParams } from "react-router-dom";
import defaultLogo from "../assets/logo.png";
import { useTenantBranding } from "../hooks/useTenantBranding";
import { useToast } from "../hooks/useToast";
import { acceptInvite, register } from "../services/authService";
import { setToken, setUser } from "../services/authSlice";
import stylesAuth from "./AuthPage.module.css";
import styles from "./Form.module.css";

type RegisterFormProps = {
  onRegistrationSuccess?: (email: string) => void;
};

const RegisterForm: React.FC<RegisterFormProps> = ({
  onRegistrationSuccess,
}) => {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [error, setError] = useState<string | null>(null);
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const { tenantSlug: urlTenantSlug } = useParams();
  const { showSuccess, showError } = useToast();
  const { logo: brandedLogo, tenantName } = useTenantBranding();
  const logo = brandedLogo || defaultLogo;

  // Check if this is an invite acceptance flow
  const inviteToken = searchParams.get("token");
  const isInviteFlow = !!inviteToken;

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();
    try {
      if (isInviteFlow && inviteToken) {
        // Handle invite acceptance flow
        const data = await acceptInvite(inviteToken, password);
        if (data.token && data.user) {
          dispatch(setToken(data.token));
          dispatch(setUser({ ...data.user, tenantSlug: urlTenantSlug }));
          const successMessage = tenantName
            ? `Welcome to ${tenantName}! Your account has been finalized.`
            : "Welcome! Your account has been finalized.";
          showSuccess(successMessage);
          // Navigate to tenant orders page
          navigate(`/${urlTenantSlug}/orders`);
        } else {
          throw new Error("Invalid response from server");
        }
      } else {
        // Handle normal registration flow
        const tenantSlug = urlTenantSlug || searchParams.get("tenant");
        const data = await register(email, password, tenantSlug);
        if (data.error) {
          setError(data.error);
          showError(data.error);
        } else if (data.requiresConfirmation) {
          // New flow: show success message and trigger email check screen
          showSuccess(data.message);
          if (onRegistrationSuccess) {
            onRegistrationSuccess(email);
          }
        } else if (data.token) {
          // Fallback: old auto-login flow (shouldn't happen with new backend)
          dispatch(setToken(data.token));
          dispatch(setUser(data.user));
          const successMessage = tenantName
            ? `Registration successful! Welcome to ${tenantName}.`
            : "Registration successful! Welcome to Monulink.";
          showSuccess(successMessage);
          navigate("/dashboard");
        }
      }
    } catch (err) {
      const message = err instanceof Error ? err.message : "An error occurred";
      setError(message);
      showError(message);
    }
  };

  return (
    <>
      <div style={{ textAlign: "center", marginBottom: "0" }}>
        <img src={logo} alt="Company Logo" className={stylesAuth.logo} />
      </div>
      {tenantName ? (
        <>
          <h2 className={stylesAuth.companyName}>{tenantName}</h2>
          <h3 className={stylesAuth.authHeader}>
            {isInviteFlow ? "Finalize Your Account" : "Register"}
          </h3>
          <div
            style={{
              backgroundColor: "#f8f9fa",
              border: "1px solid #e9ecef",
              borderRadius: "6px",
              padding: "0.75rem",
              marginBottom: "1.5rem",
              fontSize: "0.9rem",
              color: "#495057",
            }}
          >
            {isInviteFlow
              ? `Complete your account setup to access ${tenantName}`
              : `Create an account to place orders with ${tenantName}`}
          </div>
        </>
      ) : (
        <>
          <h2 className={stylesAuth.companyName}>Monulink</h2>
          <h3 className={stylesAuth.authHeader}>
            {isInviteFlow ? "Finalize Your Account" : "Register"}
          </h3>
        </>
      )}
      <form onSubmit={handleSubmit} className={styles.form}>
        {error && <p className={styles.error}>{error}</p>}
        {!isInviteFlow && (
          <input
            type="email"
            placeholder="Email"
            value={email}
            onChange={(e) => setEmail(e.target.value)}
            required
            className={styles.input}
          />
        )}
        <input
          type="password"
          placeholder={isInviteFlow ? "Choose your password" : "Password"}
          value={password}
          onChange={(e) => setPassword(e.target.value)}
          required
          className={styles.input}
        />
        <button type="submit" className={styles.submitBtn}>
          {isInviteFlow
            ? "Complete Account Setup"
            : tenantName
              ? `Register with ${tenantName}`
              : "Register"}
        </button>
      </form>
    </>
  );
};

export default RegisterForm;
